import React, { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from 'react-data-table-component';
import { Button, Input, Modal, Badge, TableActionButtons, EditIcon, DeleteIcon, DataTablePagination, DataTableCard } from '../components/ui';
import { DATA_TABLE_THEME, dataTableCustomStyles } from '../utils/dataTableTheme';

interface Product {
  id?: number;
  name: string;
  description: string;
  price: number;
  media: string[];
  status: string;
}

const formInputClass = 'w-full bg-rs-surface border border-rs-border rounded-lg text-gray-100 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-rs-accent/50 placeholder-gray-500';
const formLabelClass = 'block text-sm font-medium text-gray-300 mb-2';

export default function Products() {
  const [form, setForm] = useState<Partial<Product>>({ status: 'active', media: [] });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Partial<Product> | null>(null);

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Seguro que deseas eliminar este producto?')) return;
    setLoading(true);
    try {
      await api.delete(`/admin/products/${id}`);
      setMessage('Producto eliminado correctamente');
    } catch (err) {
      setMessage('Error al eliminar producto');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get('/admin/products').then(res => setProducts(res.data)).catch(() => setProducts([]));
  }, [message]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    setForm({ ...form, media: Array.from(files).map(f => f.name) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const payload = { ...form, price: Number(form.price), media: form.media || [] };
      await api.post('/admin/products', payload);
      setMessage('Producto creado correctamente');
      setForm({ status: 'active', media: [] });
      setModalOpen(false);
    } catch (err: any) {
      setMessage('Error al crear producto');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProduct) return;
    setLoading(true);
    setMessage('');
    try {
      const payload = { ...editProduct, price: Number(editProduct?.price), media: editProduct?.media || [] };
      await api.put(`/admin/products/${editProduct?.id}`, payload);
      setMessage('Producto actualizado correctamente');
      setEditModalOpen(false);
      setEditProduct(null);
    } catch (err: any) {
      setMessage('Error al actualizar producto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-2xl font-bold text-gray-100">Productos</h1>
        <Button onClick={() => setModalOpen(true)} leftIcon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        }>
          Nuevo producto
        </Button>
      </div>

      <DataTableCard>
      <DataTable
        columns={[
          { name: 'Nombre', selector: row => row.name, sortable: true },
          { name: 'Descripción', selector: row => row.description, sortable: true, wrap: true },
          { name: 'Precio', selector: row => `$${row.price}`, sortable: true },
          {
            name: 'Estado',
            cell: row => (
              <Badge variant={row.status === 'active' ? 'success' : 'default'}>{row.status}</Badge>
            ),
            sortable: true,
          },
          {
            name: 'Media',
            cell: row => row.media?.length ? (
              <div className="flex flex-wrap gap-1">
                {row.media.slice(0, 3).map((m, i) => (
                  <Badge key={i} variant="default">{m}</Badge>
                ))}
                {row.media.length > 3 && <span className="text-gray-500 text-xs">+{row.media.length - 3}</span>}
              </div>
            ) : '-',
          },
          {
            name: 'Acciones',
            cell: row => (
              <TableActionButtons
                actions={[
                  { label: 'Editar', icon: <EditIcon />, variant: 'edit', onClick: () => { setEditProduct(row); setEditModalOpen(true); } },
                  { label: 'Eliminar', icon: <DeleteIcon />, variant: 'delete', onClick: () => typeof row.id === 'number' && handleDelete(row.id) },
                ]}
              />
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
          },
        ]}
        data={products}
        theme={DATA_TABLE_THEME}
        customStyles={dataTableCustomStyles}
        pagination
        paginationComponent={DataTablePagination}
        highlightOnHover
        responsive
        noDataComponent={
          <div className="py-12 text-center text-gray-500">
            <p className="mb-2">No hay productos registrados</p>
            <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>Crear el primero</Button>
          </div>
        }
      />
      </DataTableCard>

      <Modal isOpen={editModalOpen} onClose={() => { setEditModalOpen(false); setEditProduct(null); }} title="Editar producto">
        {editProduct && (
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className={formLabelClass}>Nombre</label>
              <input name="name" className={formInputClass} value={editProduct?.name || ''} onChange={e => setEditProduct({ ...editProduct, name: e.target.value })} required />
            </div>
            <div>
              <label className={formLabelClass}>Descripción</label>
              <textarea name="description" className={formInputClass + ' min-h-[100px]'} value={editProduct?.description || ''} onChange={e => setEditProduct({ ...editProduct, description: e.target.value })} required />
            </div>
            <div>
              <label className={formLabelClass}>Precio</label>
              <input name="price" type="number" className={formInputClass} value={editProduct?.price || ''} onChange={e => setEditProduct({ ...editProduct, price: Number(e.target.value) })} required />
            </div>
            <div>
              <label className={formLabelClass}>Estado</label>
              <select name="status" className={formInputClass} value={editProduct?.status || 'active'} onChange={e => setEditProduct({ ...editProduct, status: e.target.value })}>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
            <div>
              <label className={formLabelClass}>Fotos o videos</label>
              <input type="file" multiple className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-rs-surfaceLight file:text-rs-accent" onChange={e => {
                const files = e.target.files;
                if (!files) return;
                setEditProduct({ ...editProduct, media: Array.from(files).map(f => f.name) });
              }} />
              {editProduct?.media && editProduct.media.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {editProduct.media.map((m, i) => (
                    <Badge key={i}>{m}</Badge>
                  ))}
                </div>
              )}
            </div>
            {message && <p className={`text-sm ${message.includes('Error') ? 'text-red-400' : 'text-rs-accent'}`}>{message}</p>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" fullWidth onClick={() => setEditModalOpen(false)}>Cancelar</Button>
              <Button type="submit" variant="secondary" fullWidth loading={loading}>Guardar</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Nuevo producto">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={formLabelClass}>Nombre</label>
            <input name="name" className={formInputClass} value={form.name || ''} onChange={handleChange} required />
          </div>
          <div>
            <label className={formLabelClass}>Descripción</label>
            <textarea name="description" className={formInputClass + ' min-h-[100px]'} value={form.description || ''} onChange={handleChange} required />
          </div>
          <div>
            <label className={formLabelClass}>Precio</label>
            <input name="price" type="number" className={formInputClass} value={form.price || ''} onChange={handleChange} required />
          </div>
          <div>
            <label className={formLabelClass}>Estado</label>
            <select name="status" className={formInputClass} value={form.status || 'active'} onChange={handleChange}>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
          <div>
            <label className={formLabelClass}>Fotos o videos</label>
            <input name="media" type="file" multiple className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-rs-surfaceLight file:text-rs-accent" onChange={handleMediaChange} />
          </div>
          {message && <p className={`text-sm ${message.includes('Error') ? 'text-red-400' : 'text-rs-accent'}`}>{message}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" fullWidth onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" fullWidth loading={loading}>Crear producto</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
